import React, { useState, useRef, useEffect } from "react";

export interface OptionItem {
    label: string;
    value: string;
    sublabel?: string;
}

interface SearchableMultiSelectProps {
    label?: string;
    placeholder?: string;
    options: OptionItem[];
    selectedValues: string[];
    onChange: (values: string[]) => void;
    disabled?: boolean;
    helpText?: string;
}

const SearchableMultiSelect: React.FC<SearchableMultiSelectProps> = ({
    label,
    placeholder = "Search and select...",
    options,
    selectedValues,
    onChange,
    disabled = false,
    helpText,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const containerRef = useRef<HTMLDivElement>(null);

    // Close dropdown on click outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const filteredOptions = options.filter((opt) =>
        opt.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (opt.sublabel && opt.sublabel.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    const handleToggleOption = (value: string) => {
        if (selectedValues.includes(value)) {
            onChange(selectedValues.filter((v) => v !== value));
        } else {
            onChange([...selectedValues, value]);
        }
    };

    const handleRemoveTag = (e: React.MouseEvent, value: string) => {
        e.stopPropagation();
        onChange(selectedValues.filter((v) => v !== value));
    };

    const selectedOptions = options.filter((opt) => selectedValues.includes(opt.value));

    return (
        <div className="form-group mb-3 position-relative" ref={containerRef}>
            {label && <label className="form-label fw-bold">{label}</label>}

            {/* Input / Badge Display Box */}
            <div
                className={`form-control d-flex flex-wrap align-items-center gap-1 ${
                    disabled ? "bg-light" : ""
                } ${isOpen ? "border-primary shadow-sm" : ""}`}
                style={{ minHeight: "42px", height: "auto", cursor: disabled ? "not-allowed" : "pointer" }}
                onClick={() => !disabled && setIsOpen(!isOpen)}
            >
                {selectedOptions.length > 0 ? (
                    selectedOptions.map((opt) => (
                        <span
                            key={opt.value}
                            className="badge bg-primary text-white d-inline-flex align-items-center gap-1 py-1 px-2 me-1"
                            style={{ fontSize: "0.85rem", fontWeight: 500 }}
                        >
                            {opt.label}
                            {!disabled && (
                                <i
                                    className="bi bi-x-lg text-white ms-1"
                                    style={{ fontSize: "0.75rem", cursor: "pointer" }}
                                    onClick={(e) => handleRemoveTag(e, opt.value)}
                                />
                            )}
                        </span>
                    ))
                ) : (
                    <span className="text-muted">{placeholder}</span>
                )}
            </div>

            {/* Dropdown container */}
            {isOpen && !disabled && (
                <div
                    className="card shadow position-absolute w-100 mt-1 border-0"
                    style={{ zIndex: 1050, maxHeight: "280px", overflow: "hidden" }}
                >
                    <div className="p-2 border-bottom bg-light">
                        <div className="input-group input-group-sm">
                            <span className="input-group-text bg-white border-end-0">
                                <i className="bi bi-search text-muted" />
                            </span>
                            <input
                                type="text"
                                className="form-control border-start-0 ps-0"
                                placeholder="Filter items..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                autoFocus
                            />
                            {searchTerm && (
                                <button
                                    className="btn btn-outline-secondary border-start-0 bg-white"
                                    type="button"
                                    onClick={() => setSearchTerm("")}
                                >
                                    <i className="bi bi-x" />
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="list-group list-group-flush overflow-auto" style={{ maxHeight: "220px" }}>
                        {filteredOptions.length === 0 ? (
                            <div className="list-group-item text-muted text-center py-3">
                                No items found
                            </div>
                        ) : (
                            filteredOptions.map((opt) => {
                                const isSelected = selectedValues.includes(opt.value);
                                return (
                                    <button
                                        type="button"
                                        key={opt.value}
                                        className={`list-group-item list-group-item-action d-flex align-items-center justify-content-between py-2 px-3 ${
                                            isSelected ? "bg-light text-primary font-weight-bold" : ""
                                        }`}
                                        onClick={() => handleToggleOption(opt.value)}
                                    >
                                        <div>
                                            <div className="fw-semibold">{opt.label}</div>
                                            {opt.sublabel && (
                                                <small className="text-muted">{opt.sublabel}</small>
                                            )}
                                        </div>
                                        <div>
                                            {isSelected ? (
                                                <i className="bi bi-check-circle-fill text-primary fs-5" />
                                            ) : (
                                                <i className="bi bi-circle text-muted fs-5" />
                                            )}
                                        </div>
                                    </button>
                                );
                            })
                        )}
                    </div>
                </div>
            )}

            {helpText && <small className="form-text text-muted mt-1 d-block">{helpText}</small>}
        </div>
    );
};

export default SearchableMultiSelect;
